import React, { useMemo } from 'react';

import { Box, Typography } from '@material-ui/core';
import { useTranslation } from 'react-i18next';

import type { ProcessingActivitiesMetrics } from 'config/api/processingActivities/processingActivities.types';
import processing_activities_messages from 'messages/processing_activities_messages';

import useStyles from '../ProcessingActivitiesPage.styles';

type Props = {
  isLoading: boolean;
  metrics?: ProcessingActivitiesMetrics;
};

type MetricTileConfig = {
  hint?: string;
  key: keyof ProcessingActivitiesMetrics;
  title: string;
  valueKey: keyof ProcessingActivitiesMetrics;
};

const ProcessingActivitiesMetricsSection: React.FC<Props> = ({ isLoading, metrics }) => {
  const { t } = useTranslation();
  const styles = useStyles();

  const tiles: MetricTileConfig[] = useMemo(
    () => [
      {
        key: 'publishedProcessingActivitiesCount',
        valueKey: 'publishedProcessingActivitiesCount',
        title: t(processing_activities_messages.metrics.published),
      },
      {
        key: 'assetsAwaitingReviewCount',
        valueKey: 'assetsAwaitingReviewCount',
        title: t(processing_activities_messages.metrics.assets_awaiting_review),
        hint: t(processing_activities_messages.metrics.assets_awaiting_review_hint),
      },
      {
        key: 'dpiaIndicationsCount',
        valueKey: 'dpiaIndicationsCount',
        title: t(processing_activities_messages.metrics.dpia_indications),
        hint: t(processing_activities_messages.metrics.dpia_indications_hint),
      },
      {
        key: 'draftsInProgressCount',
        valueKey: 'draftsInProgressCount',
        title: t(processing_activities_messages.metrics.drafts_in_progress),
      },
      {
        key: 'archivedProcessingActivitiesCount',
        valueKey: 'archivedProcessingActivitiesCount',
        title: t(processing_activities_messages.metrics.archived),
      },
    ],
    [t],
  );

  const getTileValue = (valueKey: keyof ProcessingActivitiesMetrics): string => {
    if (isLoading && !metrics) {
      return '—';
    }

    return String(metrics?.[valueKey] ?? 0);
  };

  return (
    <Box className={styles.metricsRow}>
      {tiles.map(tile => {
        const value = getTileValue(tile.valueKey);

        return (
          <Box key={tile.key} aria-label={`${tile.title}: ${value}`} className={styles.metricTile} role='group'>
            <Box className={styles.metricCard}>
              <Typography className={styles.metricLabel} color='textSecondary' variant='body2'>
                {tile.title}
              </Typography>
              <div className={styles.metricValueLine}>
                <Typography className={styles.metricValue} component='div' variant='h4'>
                  {value}
                </Typography>
              </div>
              {tile.hint && (
                <Typography className={styles.metricHint} color='textSecondary' variant='caption'>
                  {tile.hint}
                </Typography>
              )}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};

export default ProcessingActivitiesMetricsSection;
